import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import './TrafficChart.css';

const TrafficChart = ({ data, selectedMetric, filters, timeRange, hideStats }) => {
  const activeMetric = selectedMetric || 'pageviews';
  const [activeChunkIndex, setActiveChunkIndex] = useState(0);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const canvasRef = useRef(null);

  const metrics = useMemo(() => ({
    pageviews: { label: 'Pageviews', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
    users: { label: 'Users', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
    sessions: { label: 'Sessions', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)' },
    engagedSessions: { label: 'Engaged Sessions', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)' },
    engagementRate: { label: 'Engagement Rate', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)' },
    avgEngagement: { label: 'Avg. Engagement', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)' },
    engagementTime: { label: 'Engagement Time', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)' },
    returningUserEngagementTime: { label: 'Returning User Engagement Time', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)' },
    bounceRate: { label: 'Bounce Rate', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' },
    newUsers: { label: 'New Users', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)' },
    returnUsers: { label: 'Return Users', color: '#f97316', bgColor: 'rgba(249, 115, 22, 0.1)' },
    usersPerSession: { label: 'Users per Session', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)' },
    pageviewsPerUser: { label: 'Pageviews per User', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
    returningUsersPercentage: { label: '% Returning Visitors', color: '#a855f7', bgColor: 'rgba(168, 85, 247, 0.1)', isPercentage: true },
    engagedSessionsPerUser: { label: 'Engaged Sessions/User', color: '#a855f7', bgColor: 'rgba(168, 85, 247, 0.1)' },
    desktopPercentage: { label: 'Desktop Users', color: '#84cc16', bgColor: 'rgba(132, 204, 22, 0.1)' },
    mobilePercentage: { label: 'Mobile Users', color: '#84cc16', bgColor: 'rgba(132, 204, 22, 0.1)' },
    tabletPercentage: { label: 'Tablet Users', color: '#84cc16', bgColor: 'rgba(132, 204, 22, 0.1)' },
    impressions: { label: 'Search Impressions', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)' },
    clicks: { label: 'Search Clicks', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)' },
    ctr: { label: 'CTR', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)' },
    adImpressions: { label: 'Ad Impressions', color: '#6366f1', bgColor: 'rgba(99, 102, 241, 0.1)' },
    revenue: { label: 'Revenue', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)', isCurrency: true },
    rpm: { label: 'RPM', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)', isCurrency: true },
    cpm: { label: 'CPM', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)', isCurrency: true },
    rpu: { label: 'RPU', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)', isCurrency: true }
  }), []);

  // Calculate days from timeRange (handle both numeric and custom range objects)
  const getTimeRangeDays = () => {
    if (typeof timeRange === 'object' && timeRange?.custom) {
      const startDate = new Date(timeRange.startDate);
      const endDate = new Date(timeRange.endDate);
      const diffTime = Math.abs(endDate - startDate);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }
    return typeof timeRange === 'number' ? timeRange : (data?.length || 0);
  };
  
  const shouldAggregateMonthly = getTimeRangeDays() > 90 || (data?.length || 0) > 90;

  const monthlyData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const monthMap = new Map();

    data.forEach((entry) => {
      if (!entry || !entry.date) return;
      const dateObj = new Date(entry.date);
      if (Number.isNaN(dateObj.getTime())) return;

      const key = `${dateObj.getFullYear()}-${(dateObj.getMonth() + 1)
        .toString()
        .padStart(2, '0')}`;

      if (!monthMap.has(key)) {
        monthMap.set(key, {
          date: new Date(dateObj.getFullYear(), dateObj.getMonth(), 1)
            .toISOString()
            .split('T')[0],
          users: 0,
          sessions: 0,
          pageviews: 0,
          engagedSessions: 0,
        totalSessionDuration: 0,
        durationWeight: 0
        });
      }

      const group = monthMap.get(key);
      group.users += entry.users || 0;
      group.sessions += entry.sessions || 0;
      group.pageviews += entry.pageviews || 0;
      group.engagedSessions += entry.engagedSessions || 0;

      // Handle engagement time from various possible fields
      const engagementDuration = entry.avgSessionDuration || entry.avgEngagementSeconds || entry.engagementTime || entry.avgEngagement || 0;
      if (engagementDuration !== undefined && engagementDuration !== null && engagementDuration > 0) {
        const weight = entry.sessions || 0;
        group.totalSessionDuration += engagementDuration * (weight || 1);
        group.durationWeight += weight || 1;
      }
    });

    return Array.from(monthMap.values())
      .map((group) => {
        const avgDuration = group.durationWeight > 0
          ? group.totalSessionDuration / group.durationWeight
          : 0;
        return {
        date: group.date,
        users: group.users,
        sessions: group.sessions,
        pageviews: group.pageviews,
        engagedSessions: group.engagedSessions,
          avgSessionDuration: avgDuration,
          avgEngagementSeconds: avgDuration,
          engagementTime: avgDuration,
          avgEngagement: avgDuration
        };
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [data]);

  const processedData = useMemo(() => {
    if (shouldAggregateMonthly) {
      return monthlyData;
    }
    return data || [];
  }, [data, monthlyData, shouldAggregateMonthly]);

  const chunkSize = 30;
  const showChunkButtons =
    !shouldAggregateMonthly && processedData && processedData.length > chunkSize;
  const chunkCount = showChunkButtons
    ? Math.ceil(processedData.length / chunkSize)
    : 1;

  useEffect(() => {
    if (showChunkButtons && chunkCount > 0) {
      setActiveChunkIndex(chunkCount - 1);
    } else {
      setActiveChunkIndex(0);
    }
  }, [showChunkButtons, chunkCount]);

  const displayData = useMemo(() => {
    if (!processedData) return [];
    if (showChunkButtons && chunkCount > 0) {
      const startIndex = activeChunkIndex * chunkSize;
      const endIndex = Math.min(startIndex + chunkSize, processedData.length);
      return processedData.slice(startIndex, endIndex);
    }
    return processedData;
  }, [processedData, showChunkButtons, chunkCount, activeChunkIndex]);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return 'Invalid Date';

    let date;
    if (typeof dateStr === 'string') {
      if (dateStr.includes('T')) {
        date = new Date(dateStr);
      } else if (dateStr.includes('-')) {
        date = new Date(dateStr);
      } else if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        date = new Date(`${year}-${month}-${day}`);
      } else {
        date = new Date(dateStr);
      }
    } else {
      date = new Date(dateStr);
    }

    if (Number.isNaN(date.getTime())) {
      console.error('Invalid date:', dateStr);
      return 'Invalid Date';
    }

    return date.toLocaleDateString(
      'en-US',
      shouldAggregateMonthly
        ? { month: 'short', year: 'numeric' }
        : { month: 'short', day: 'numeric' }
    );
  }, [shouldAggregateMonthly]);

  const chunkButtons = useMemo(() => {
    if (!showChunkButtons || chunkCount <= 1 || !processedData) return [];

    const buttons = [];
    for (let i = 0; i < chunkCount; i += 1) {
      const startIndex = i * chunkSize;
      const endIndex = Math.min(startIndex + chunkSize, processedData.length);
      const startEntry = processedData[startIndex];
      const endEntry = processedData[endIndex - 1];

      if (!startEntry || !endEntry) continue;

      const label = `${formatDate(startEntry.date)} – ${formatDate(endEntry.date)}`;

      buttons.push({ label, index: i });
    }
    return buttons;
  }, [showChunkButtons, chunkCount, processedData, formatDate]);

  const chartData = useMemo(() => displayData || [], [displayData]);

  // Draw the chart
  useEffect(() => {
    if (!chartData || chartData.length === 0) {
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Set canvas size to full container size
    const containerHeight = 420; // Match the CSS height
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = containerHeight * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    // Clear canvas
    ctx.clearRect(0, 0, rect.width, containerHeight);

    const width = rect.width;
    const height = containerHeight;
    const padding = 60;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Get values for the selected metric
    const values = chartData.map(d => {
      let value;
      
      // Calculate derived metrics on the fly
      if (activeMetric === 'usersPerSession') {
        value = d.users && d.users > 0 ? d.sessions / d.users : 0;
      } else if (activeMetric === 'pageviewsPerUser') {
        value = d.users && d.users > 0 ? d.pageviews / d.users : 0;
      } else if (activeMetric === 'rpu') {
        // RPU = Revenue / Users
        value = d.users && d.users > 0 ? (d.revenue || 0) / d.users : 0;
      } else if (activeMetric === 'rpm' || activeMetric === 'cpm') {
        // RPM and CPM come directly from the data
        value = d[activeMetric] || 0;
      } else if (activeMetric === 'avgEngagement' || activeMetric === 'engagementTime') {
        // Map avgEngagementSeconds to avgEngagement/engagementTime (site-wide)
        value = d.avgEngagementSeconds || d.avgEngagement || 0;
      } else if (activeMetric === 'returningUserEngagementTime') {
        // Returning user engagement time from daily data
        value = d.returningUserEngagementTime || d.avgEngagementSeconds || d.avgEngagement || 0;
      } else if (activeMetric === 'engagedSessionsPerUser') {
        // Engaged sessions per user from daily data
        value = d.engagedSessionsPerUser || 0;
      } else if (activeMetric === 'returnUsers') {
        // Returning users from daily data
        value = d.returnUsers || d.returningUsers || 0;
      } else if (activeMetric === 'returningUsersPercentage') {
        // Returning users percentage
        const totalUsers = d.users || 0;
        const returningUsers = d.returnUsers || d.returningUsers || 0;
        value = totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0;
      } else {
        value = d[activeMetric] || 0;
      }
      
      // Handle different metric types
      if (activeMetric === 'engagementRate' || activeMetric === 'bounceRate' || activeMetric === 'ctr' || activeMetric === 'desktopPercentage' || activeMetric === 'mobilePercentage' || activeMetric === 'tabletPercentage' || activeMetric === 'returningUsersPercentage') {
        // These are percentages, already numeric
        return value;
      } else if (activeMetric === 'avgEngagement' || activeMetric === 'engagementTime' || activeMetric === 'returningUserEngagementTime') {
        // This is time in seconds, already numeric
        return value;
      } else if (activeMetric === 'usersPerSession' || activeMetric === 'pageviewsPerUser' || activeMetric === 'engagedSessionsPerUser') {
        // These are ratios, already calculated
        return value;
      } else {
        // Users, sessions, pageviews, engagedSessions, impressions, clicks, returnUsers, newUsers, adImpressions are already numeric
        return value;
      }
    });
    
    const maxValue = Math.max(...values);
    
    // Calculate nice round numbers for Y-axis (same logic as labels)
    const getNiceNumber = (value) => {
      if (value === 0) return 1; // Handle zero case
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;
      
      if (normalized <= 1) return magnitude;
      if (normalized <= 2) return 2 * magnitude;
      if (normalized <= 5) return 5 * magnitude;
      return 10 * magnitude;
    };
    
    const chartMin = 0;
    const chartMax = maxValue > 0 ? getNiceNumber(maxValue * 1.1) : 1; // Add 10% padding
    const chartRange = chartMax - chartMin;

    // Draw grid lines
    ctx.strokeStyle = '#f3f4f6';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    
    for (let i = 0; i <= 5; i++) {
      const y = padding + (i / 5) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }

    // Reset line dash
    ctx.setLineDash([]);

    // Draw axes
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    
    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(width - padding, height - padding);
    ctx.stroke();
    
    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, height - padding);
    ctx.stroke();

    // Draw X-axis markers (tick marks)
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    
    const denominator = Math.max(chartData.length - 1, 1);

    for (let i = 0; i < chartData.length; i++) {
      const x = padding + (i / denominator) * chartWidth;
      ctx.beginPath();
      ctx.moveTo(x, height - padding);
      ctx.lineTo(x, height - padding + 5);
      ctx.stroke();
    }

    // Calculate points
    const points = chartData.map((d, i) => {
      const x = padding + (i / denominator) * chartWidth;
      const value = values[i]; // Use calculated value
      const normalizedValue = (value - chartMin) / chartRange;
      const y = height - padding - (normalizedValue * chartHeight);
      return { x, y, value };
    });

    // Draw area fill
    const gradient = ctx.createLinearGradient(0, padding, 0, height - padding);
    gradient.addColorStop(0, metrics[activeMetric].bgColor);
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(points[0].x, height - padding);
    points.forEach(point => ctx.lineTo(point.x, point.y));
    ctx.lineTo(points[points.length - 1].x, height - padding);
    ctx.closePath();
    ctx.fill();

    // Draw line
    ctx.strokeStyle = metrics[activeMetric].color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    points.slice(1).forEach(point => ctx.lineTo(point.x, point.y));
    ctx.stroke();

    // Draw data points
    ctx.fillStyle = metrics[activeMetric].color;
    points.forEach((point, index) => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 4, 0, 2 * Math.PI);
      ctx.fill();
      
      // White border for points
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Store point data for hover
      point.index = index;
      point.date = chartData[index].date;
    });

    // Add hover event listeners
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Find closest point
      let closestPoint = null;
      let minDistance = Infinity;
      
      points.forEach(point => {
        const distance = Math.sqrt((x - point.x) ** 2 + (y - point.y) ** 2);
        if (distance < 20 && distance < minDistance) {
          minDistance = distance;
          closestPoint = point;
        }
      });
      
      setHoveredPoint(closestPoint);
    };
    
    const handleMouseLeave = () => {
      setHoveredPoint(null);
    };
    
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };

  }, [chartData, activeMetric, filters, metrics]);

  // Generate Y-axis labels with standard even values
  const generateYAxisLabels = () => {
    if (!data || data.length === 0) return [];
    
    const values = data.map(d => {
      if (activeMetric === 'usersPerSession') {
        return d.users && d.users > 0 ? d.sessions / d.users : 0;
      } else if (activeMetric === 'pageviewsPerUser') {
        return d.users && d.users > 0 ? d.pageviews / d.users : 0;
      } else if (activeMetric === 'rpu') {
        return d.users && d.users > 0 ? (d.revenue || 0) / d.users : 0;
      } else if (activeMetric === 'engagedSessionsPerUser') {
        return d.engagedSessionsPerUser || 0;
      } else if (activeMetric === 'returnUsers') {
        return d.returnUsers || d.returningUsers || 0;
      } else if (activeMetric === 'avgEngagement' || activeMetric === 'engagementTime') {
        return d.avgEngagementSeconds || d.avgEngagement || d.engagementTime || 0;
      } else if (activeMetric === 'returningUserEngagementTime') {
        return d.returningUserEngagementTime || d.avgEngagementSeconds || d.avgEngagement || 0;
      } else if (activeMetric === 'returningUsersPercentage') {
        const totalUsers = d.users || 0;
        const returningUsers = d.returnUsers || d.returningUsers || 0;
        return totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0;
      } else {
        return d[activeMetric] || 0;
      }
    });
    const maxValue = Math.max(...values);
    
    // Calculate nice round numbers for Y-axis
    const getNiceNumber = (value) => {
      if (value === 0) return 1; // Handle zero case
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;
      
      if (normalized <= 1) return magnitude;
      if (normalized <= 2) return 2 * magnitude;
      if (normalized <= 5) return 5 * magnitude;
      return 10 * magnitude;
    };
    
    const niceMax = maxValue > 0 ? getNiceNumber(maxValue * 1.1) : 1; // Add 10% padding
    const step = niceMax / 5; // 5 grid lines
    
    const labels = [];
    for (let i = 0; i <= 5; i++) {
      const value = step * i;
      
      // Format display value based on metric type
      let displayValue;
      const metric = metrics[activeMetric];
      if (metric?.isCurrency) {
        // Format currency values
        if (value >= 1000) {
          displayValue = `$${(value / 1000).toFixed(2)}K`;
        } else {
          displayValue = `$${value.toFixed(2)}`;
        }
      } else if (activeMetric === 'engagementRate' || activeMetric === 'bounceRate' || activeMetric === 'ctr' || activeMetric === 'desktopPercentage' || activeMetric === 'mobilePercentage' || activeMetric === 'tabletPercentage' || activeMetric === 'returningUsersPercentage') {
        displayValue = `${value.toFixed(1)}%`;
      } else if (activeMetric === 'avgEngagement' || activeMetric === 'engagementTime' || activeMetric === 'returningUserEngagementTime') {
        // Format time duration
        const minutes = Math.floor(value / 60);
        const seconds = Math.floor(value % 60);
        if (minutes > 0) {
          displayValue = `${minutes}m ${seconds}s`;
        } else {
          displayValue = `${seconds}s`;
        }
      } else if (activeMetric === 'usersPerSession' || activeMetric === 'pageviewsPerUser' || activeMetric === 'engagedSessionsPerUser') {
        displayValue = value.toFixed(2);
      } else {
        // Format numbers with K/M suffixes
        if (value >= 1000000) {
          displayValue = `${(value / 1000000).toFixed(1)}M`;
        } else if (value >= 1000) {
          displayValue = `${(value / 1000).toFixed(1)}K`;
        } else {
          displayValue = Math.round(value).toString();
        }
      }
      
      labels.push({ 
        value: Math.round(value), 
        displayValue
      });
    }
    return labels;
  };

  const yAxisLabels = generateYAxisLabels();

  // Format value for display based on metric type
  const formatValue = (value) => {
    const metric = metrics[activeMetric];
    if (metric?.isCurrency) {
      if (value >= 1000) {
        return `$${(value / 1000).toFixed(2)}K`;
      }
      return `$${value.toFixed(2)}`;
    }
    if (activeMetric === 'engagementRate' || activeMetric === 'bounceRate' || activeMetric === 'ctr' || activeMetric === 'desktopPercentage' || activeMetric === 'mobilePercentage' || activeMetric === 'tabletPercentage' || activeMetric === 'returningUsersPercentage') {
      return `${value.toFixed(1)}%`;
    } else if (activeMetric === 'avgEngagement' || activeMetric === 'engagementTime' || activeMetric === 'returningUserEngagementTime') {
      const minutes = Math.floor(value / 60);
      const seconds = Math.floor(value % 60);
      if (minutes > 0) {
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      } else {
        return `${seconds}s`;
      }
    } else if (activeMetric === 'usersPerSession' || activeMetric === 'pageviewsPerUser' || activeMetric === 'engagedSessionsPerUser' || activeMetric === 'rpu' || activeMetric === 'rpm' || activeMetric === 'cpm') {
      return value.toFixed(2);
    } else {
      // For integers, use toLocaleString. For decimals, round to 2 decimal places first
      if (value % 1 === 0) {
        return value.toLocaleString();
      } else {
        return value.toFixed(2);
      }
    }
  };

  // Format exact value for tooltip (no rounding or K/M suffixes)
  const formatExactValue = (value) => {
    const metric = metrics[activeMetric];
    if (metric?.isCurrency) {
      return `$${value.toFixed(2)}`;
    }
    if (activeMetric === 'engagementRate' || activeMetric === 'bounceRate' || activeMetric === 'ctr' || activeMetric === 'desktopPercentage' || activeMetric === 'mobilePercentage' || activeMetric === 'tabletPercentage' || activeMetric === 'returningUsersPercentage') {
      return `${value.toFixed(2)}%`;
    } else if (activeMetric === 'avgEngagement' || activeMetric === 'engagementTime' || activeMetric === 'returningUserEngagementTime') {
      const minutes = Math.floor(value / 60);
      const seconds = Math.floor(value % 60);
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    } else if (activeMetric === 'usersPerSession' || activeMetric === 'pageviewsPerUser' || activeMetric === 'engagedSessionsPerUser' || activeMetric === 'rpu' || activeMetric === 'rpm' || activeMetric === 'cpm') {
      return value.toFixed(2);
    } else {
      // Show exact value with commas for thousands
      if (value % 1 === 0) {
        return value.toLocaleString();
      } else {
        // Show decimals with appropriate precision
        return value.toLocaleString('en-US', { 
          minimumFractionDigits: 0, 
          maximumFractionDigits: 2 
        });
      }
    }
  };

  // Format date for display
  if (!chartData || chartData.length === 0) {
    return (
      <div className="traffic-chart">
        <div className="chart-header">
          <h3>
            {metrics[activeMetric].label} Trend
            {filters?.geo && filters.geo !== 'all' && ` (${filters.geo})`}
            {filters?.device && filters.device !== 'all' && ` - ${filters.device.charAt(0).toUpperCase() + filters.device.slice(1)}`}
            {filters?.trafficSource && filters.trafficSource !== 'all' && ` - ${filters.trafficSource.charAt(0).toUpperCase() + filters.trafficSource.slice(1)}`}
          </h3>
        </div>
        <div className="no-chart-data">
          <p>No data available for chart</p>
        </div>
      </div>
    );
  }

  // Calculate statistics - need to recalculate values for derived metrics
  const values = chartData.map(d => {
    if (activeMetric === 'usersPerSession') {
      return d.users && d.users > 0 ? d.sessions / d.users : 0;
    } else if (activeMetric === 'pageviewsPerUser') {
      return d.users && d.users > 0 ? d.pageviews / d.users : 0;
    } else if (activeMetric === 'rpu') {
      return d.users && d.users > 0 ? (d.revenue || 0) / d.users : 0;
    } else if (activeMetric === 'rpm' || activeMetric === 'cpm') {
      return d[activeMetric] || 0;
    } else if (activeMetric === 'avgEngagement' || activeMetric === 'engagementTime') {
      // Map avgEngagementSeconds to avgEngagement/engagementTime (site-wide)
      return d.avgEngagementSeconds || d.avgEngagement || 0;
    } else if (activeMetric === 'returningUserEngagementTime') {
      // Returning user engagement time from daily data
      return d.returningUserEngagementTime || d.avgEngagementSeconds || d.avgEngagement || 0;
    } else if (activeMetric === 'engagedSessionsPerUser') {
      // Engaged sessions per user from daily data
      return d.engagedSessionsPerUser || 0;
    } else if (activeMetric === 'returnUsers') {
      // Returning users from daily data
      return d.returnUsers || d.returningUsers || 0;
    } else if (activeMetric === 'returningUsersPercentage') {
      // Returning users percentage
      const totalUsers = d.users || 0;
      const returningUsers = d.returnUsers || d.returningUsers || 0;
      return totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0;
    } else {
      return d[activeMetric] || 0;
    }
  });
  const total = values.reduce((a, b) => a + b, 0);
  const average = values.length > 0 ? total / values.length : 0;
  const peak = values.length > 0 ? Math.max(...values) : 0;

  // Safety check - ensure metric exists
  if (!metrics[activeMetric]) {
    console.error(`Metric "${activeMetric}" not found in metrics object. Available metrics:`, Object.keys(metrics));
    return (
      <div className="traffic-chart">
        <div className="chart-error">
          <p>Error: Metric "{activeMetric}" is not available for charting.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="traffic-chart">
      <div className="chart-header">
        <h3>
          {metrics[activeMetric].label} Trend
          {filters?.geo && filters.geo !== 'all' && ` (${filters.geo})`}
          {filters?.device && filters.device !== 'all' && ` - ${filters.device.charAt(0).toUpperCase() + filters.device.slice(1)}`}
          {filters?.trafficSource && filters.trafficSource !== 'all' && ` - ${filters.trafficSource.charAt(0).toUpperCase() + filters.trafficSource.slice(1)}`}
        </h3>
        {showChunkButtons && chunkButtons.length > 0 && (
          <div className="chart-chunk-controls">
            {chunkButtons.map((button) => (
              <button
                key={button.index}
                type="button"
                className={`chunk-button ${button.index === activeChunkIndex ? 'active' : ''}`}
                onClick={() => setActiveChunkIndex(button.index)}
              >
                {button.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="chart-container">
        <div className="chart-wrapper">
          {/* Y-axis labels */}
          <div className="y-axis-labels">
            {yAxisLabels.map((label, i) => (
              <div key={i} className="y-label" style={{ 
                top: `${60 + ((5 - i) / 5) * 300}px`,
                transform: 'translateY(-50%)'
              }}>
                {label.displayValue}
              </div>
            ))}
          </div>
          
          {/* Chart Canvas */}
          <div className="chart-canvas-container">
            <canvas 
              ref={canvasRef}
              className="chart-canvas"
            />
            {hoveredPoint && (
              <div 
                className="chart-hover-tooltip"
                style={{
                  left: `${hoveredPoint.x}px`,
                  top: `${hoveredPoint.y - 60}px`
                }}
              >
                <div className="hover-tooltip-content">
                  <div className="hover-date">{formatDate(hoveredPoint.date)}</div>
                  <div className="hover-value">{formatExactValue(hoveredPoint.value)} {metrics[activeMetric].label}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* X-axis labels */}
        <div className="chart-labels">
          <span>{formatDate(chartData[0].date)}</span>
          {chartData.length > 2 && (
            <span>{formatDate(chartData[Math.floor(chartData.length / 2)].date)}</span>
          )}
          <span>{formatDate(chartData[chartData.length - 1].date)}</span>
        </div>
      </div>

      {/* Chart statistics */}
      {!hideStats && (
        <div className="chart-stats">
          {!['usersPerSession', 'pageviewsPerUser', 'avgEngagement', 'engagementRate', 'bounceRate', 'desktopPercentage', 'mobilePercentage', 'tabletPercentage'].includes(activeMetric) && (
          <div className="stat">
            <span className="stat-label">Total</span>
              <span className="stat-value">{formatValue(total)}</span>
          </div>
          )}
          <div className="stat">
            <span className="stat-label">Average</span>
            <span className="stat-value">{formatValue(average)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Peak</span>
            <span className="stat-value">{formatValue(peak)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficChart;